import React from 'react';
import { Link } from "react-router-dom";
import Highlight from 'src/components/Highlight';
import routes from 'src/routes';
import url from 'src/util/url';
import Page from 'src/components/Page';
import ExpandDoc from 'src/components/ExpandDoc';
import CodeSamples from 'src/components/CodeSamples';

const onError = {
    type: 'function',
    description: 'A callback function that is executed when an error occurs',
    mandatory: true,
    children: {
        code: {
            type: "string",
            description: <>One of <code>apiError</code>, <code>klarnaError</code>, or <code>klarnaException</code></>
        },
        data: {
            type: "object",
            description: "Corresponding error object",
            children: {
                code: {
                    type: "string",
                    description: <>API response code. If exists, one of <code>success</code>, <code>error</code>, or <code>timeout</code></>
                },
                data: {
                    type: "object",
                    description: "API response error data"
                },
                response: {
                    type: "object",
                    description: "Klarna response error object"
                },
                exception: {
                    type: "error",
                    description: "Exception thrown by Klarna library"
                }
            }
        }
    }
};

function TxnKlarnaPage() {
    return <Page header="Klarna">
        <p>
            Klarna is a buy now and pay later service.  You can offer this service to your customers.  Please contact your Relationship Manager or our Merchant Support Helpdesk on 1800 230 177 
        (option 1, 3, 1) Monday to Friday 9am to 5pm (AEST) for more information.
        </p>
        <h2>How to integrate Klarna</h2>
        <p>
            In order to process a payment via Klarna, you must:
        </p>
        <h3>Step 1. <Link to={routes.api.spec + "#operation/TxnAuthkey_CreateAuthkey"}>Create txn authkey</Link></h3>
        <h3>Step 2. <Link to={routes.api.spec + "#operation/TxnAuthkey_AttachTxnDetails"}>Attach txn details</Link></h3>
        <h3>Step 3. <Link to={routes.api.spec + "#operation/TxnAuthkey_AttachOrderDetails"}>Attach order details</Link></h3>
        <code>items</code> array must be supplied, there must be at least 1 item,
        and each item must have the <code>description</code>, <code>quantity</code>, <code>unitPrice</code>, and <code>totalAmount</code> properties supplied
        <h3>Step 4. <Link to={routes.api.spec + "#operation/TxnAuthkey_AttachPaymentMethod"}>Attach payment method</Link></h3>
        <code>provider</code> of <code>Klarna</code> must be supplied
        <h3>Step 5. <Link to={routes.api.spec + "#operation/TxnAuthkey_InitiateProvider"}>Initate payment method provider</Link></h3>
        <h3>Step 6. Integrate with Klarna JavaScript</h3>
        <ol>
            <li>Call <code>BPOINT.txn.authkey.klarna.init</code> setup and retrieve payment method categories</li>
            <li>Call <code>BPOINT.txn.authkey.klarna.load</code> to select a payment method category and display the Klarna widget</li>
            <li>Call <code>BPOINT.txn.authkey.klarna.authorize</code> to start the authorisation process. Customer will then fill out the payment information</li>
        </ol>
        <h3>Step 7. <Link to={routes.api.spec + "#operation/TxnAuthkey_Process"}>Process payment</Link></h3>

        <h2>JavaScript</h2>
        <p>To enable JavaScript integration, you must include the following script tag in your page.</p>
        <Highlight language={'html'}>
            {`<script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>`}
        </Highlight>

        <h2 id="Specification">Specification</h2>
        <ExpandDoc
            method="BPOINT.txn.authkey.klarna.init"
            doc={{
                authkey: {
                    type: 'string',
                    description: "Transaction authkey to process under. Initiate provider must have been called prior to this."
                },
                options: {
                    type: "object",
                    description: "Configuration options",
                    children: {
                        onComplete: {
                            type: "function",
                            description: "A callback function that is executed when initalisation is complete",
                            mandatory: true,
                            children: {
                                response: {
                                    type: "object",
                                    description: "Response object",
                                    children: {
                                        paymentMethodCategories: {
                                            type: "array",
                                            description: "List of payment method categories",
                                            children: {
                                                identifier: {
                                                    type: "string",
                                                    description: "Unique identifier to pass into BPOINT.txn.authkey.klarna.load"
                                                },
                                                name: {
                                                    type: "string",
                                                    description: "Payment method category name"
                                                },
                                                logoUrl: {
                                                    type: "string",
                                                    description: "URL of the payment method category's logo"
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        },
                        onError,
                    }
                }
            }}
        />

        <ExpandDoc
            method="BPOINT.txn.authkey.klarna.load"
            doc={{
                options: {
                    type: "object",
                    description: "Configuration options",
                    children: {
                        container: {
                            type: ["string", "element"],
                            description: "CSS selector, or a HTML element to be used as container for the Klarna widget",
                            mandatory: true,
                        },
                        paymentMethodCategory: {
                            type: "string",
                            description: <>Identifier of one of the payment method categories returned in the <code>BPOINT.txn.authkey.klarna.init</code> call</>,
                            mandatory: true,
                        },
                        onComplete: {
                            type: "function",
                            description: "A callback function that is executed when Klarna has loaded",
                            mandatory: true,
                        },
                        onError,
                    }
                }
            }}
        />

        <ExpandDoc
            method="BPOINT.txn.authkey.klarna.authorize"
            doc={{
                options: {
                    type: "object",
                    description: "Configuration options",
                    children: {
                        onComplete: {
                            type: "function",
                            description: "A callback function that is executed when Klarna has loaded",
                            mandatory: true,
                        },
                        onError,
                    }
                }
            }}
        />

        <ExpandDoc
            method="BPOINT.txn.authkey.getKlarnaSession"
            doc={{
                authkey: {
                    type: 'string',
                    description: "Transaction authkey to attach payment method to"
                },
                callback: {
                    type: "function",
                    description: "A callback function that is executed when the submission result is received",
                    children: {
                        code: {
                            type: "string",
                            description: <>One of <code>success</code>, <code>error</code>, or <code>timeout</code></>
                        },
                        data: {
                            type: "object",
                            description: <>See response of <Link to={routes.api.spec + "#operation/TxnAuthkeyClient_GetKlarnaSession"}>TokenAuthkeyClient - GetKlarnaSession</Link></>
                        }
                    }
                }
            }}
        />
        <h3>Example</h3>
        <p>Below is a template for implementing HTML and JavaScript components for Klarna.</p>
        <CodeSamples
            samples={[
                {
                    language: "html",
                    content: `
<div id="klarnaWidgetContainer"></div>

<button id="checkoutButton" disabled>Checkout</button>
`
                }]} copyButton />

<CodeSamples
            samples={[
                {
                    language: "javascript",
                    content: `
BPOINT.txn.authkey.klarna.init(authkey, {
    appendToElementId: "cardForm",
    onComplete: function (res) {
        // - If you have multiple payment method categories,
        // you may choose to show the user a form to select from.
        // - Alternatively, if you already know the category,
        // you may proceed to call .load with your category

        BPOINT.txn.authkey.klarna.load({
            // for this example, we are just using the first category
            // (this may be the only option)
            paymentMethodCategory: res.paymentMethodCategories[0].identifier,
            container: "#klarnaWidgetContainer",
            onComplete: function (res) {
                // at this point, the Klarna widget will be populated with payment information
                // you should enable/add a button which allow the user to confirm,
                // before proceeding to authorisation step
                document.querySelector("#checkoutButton").disabled = false;
            },
            onError: function (code, data) {
                // show error to user
            }
        })
    },
    onError: function (code, data) {
        // show error to user
    }
});


// .authorize should be called after .init and .load have been completed,
// when the user confirms to authorise
// for example, this could be called when a "checkout" button has been pressed
document.querySelector("#checkoutButton").addEventListener(function () {
    BPOINT.txn.authkey.klarna.authorize(authkey, {
        onComplete: function (res) {
            // Klarna has been succesfully authorised, proceed to next step
        },
        onError: function (code, data) {
            // show error to user
        }
    });
});
`
                }
            ]} copyButton
        />
    </Page>
}

export default TxnKlarnaPage;